import type { EventManager } from '@arkturian/audio-guide';
import type { IView } from '../IView';

import templateHtml from './template.html?raw';
import './styles.css';
import './KnowledgeView.css';

import { TopicCardView } from '../default/TopicCardView';
import { VibePillView } from '../default/VibePillView';
import { SpecialTrackView } from '../default/SpecialTrackView';
import { YoutubePanelView } from '../default/YoutubePanelView';
import { KnowledgeView, type KnowledgeData } from './KnowledgeView';
import { t } from '@/i18n';

/**
 * Knowledge-API host for fetching the full species record when the bot
 * tells us a topic has knowledge_id attached. Read through
 * getKnowledgeBase() so callers can override via the library's
 * configureKnowledgeBase() export (Tschepp's recommendation 2026-06-04
 * — same pattern as configureStorageBase from AP4).
 */
import { getKnowledgeBase } from '@arkturian/audio-guide/lib/knowledgeBase';

/** In-memory species-data cache so the same binom isn't fetched twice. */
const knowledgeCache = new Map<string, KnowledgeData>();

/** Shape of the bits we read from artrack /waypoints/{id}. The endpoint
 *  returns more, but for the topic-card → KnowledgeView wiring we only
 *  need to know whether the waypoint has a knowledge_id attached. The
 *  Knowledge-API takes over from there. */
interface ArtrackWaypoint {
  id: number;
  metadata_json?: {
    knowledge_id?: number | null;
    title?: string;
  };
}

const waypointCache = new Map<number, ArtrackWaypoint | null>();
const ARTRACK_API_BASE = 'https://api-artrack.arkturian.com';
const ARTRACK_API_KEY = 'Inetpass1';

/** Resolve an artrack waypoint by id. Used when the bot sets
 *  topic.waypoint_id but no topic.knowledge_id — we look up the
 *  waypoint to see if it carries a linked Knowledge entry (the
 *  curator may have wired one in metadata_json.knowledge_id) and,
 *  if so, light up the KnowledgeView path with the discovered kid.
 *
 *  Returns null on any failure (not found / api key invalid / network).
 *  Caches per id including null results, so a failed lookup doesn't
 *  retry on every re-render. */
async function fetchArtrackWaypoint(id: number): Promise<ArtrackWaypoint | null> {
  if (waypointCache.has(id)) return waypointCache.get(id) ?? null;
  try {
    const r = await fetch(`${ARTRACK_API_BASE}/waypoints/${id}`, {
      headers: { 'X-API-KEY': ARTRACK_API_KEY },
      credentials: 'omit',
    });
    if (!r.ok) {
      waypointCache.set(id, null);
      return null;
    }
    const data = (await r.json()) as ArtrackWaypoint;
    waypointCache.set(id, data);
    return data;
  } catch (err) {
    console.warn('fetchArtrackWaypoint failed', err);
    waypointCache.set(id, null);
    return null;
  }
}

async function fetchKnowledge(key: { binom?: string; knowledge_id?: number }): Promise<KnowledgeData | null> {
  const cacheKey = key.binom || (key.knowledge_id != null ? `id:${key.knowledge_id}` : '');
  if (!cacheKey) return null;
  const cached = knowledgeCache.get(cacheKey);
  if (cached) return cached;
  try {
    let url: string;
    if (key.binom) {
      url = `${getKnowledgeBase()}/api/v1/knowledge/species/${encodeURIComponent(key.binom)}`;
    } else {
      // Fallback: per-id lookup via content-api post (Knowledge has no
      // by-id endpoint yet — guide-api guide.md notes this).
      url = `${getKnowledgeBase()}/api/v1/knowledge/annotated/${key.knowledge_id}`;
    }
    const r = await fetch(url, { credentials: 'omit' });
    if (!r.ok) return null;
    const data = await r.json() as KnowledgeData;
    knowledgeCache.set(cacheKey, data);
    return data;
  } catch (err) {
    console.warn('fetchKnowledge failed', err);
    return null;
  }
}

/**
 * ImmersiveSkin v4 — single-screen flow.
 *
 * The screen never changes layout. Same structure always:
 *   - Map (fullscreen, GPS-centered)
 *   - Center area: welcome text → status text → fades out
 *   - Bottom: actions (START + music + settings) — stays
 *   - Audio panel slides up when guide speaks
 *
 * No top bar. No bottom button bar. Stop/Pause live in Settings modal.
 */
/** Replace a Lucide icon inside an element */
function setLucideIcon(el: HTMLElement | null, iconName: string, size: number = 16): void {
  if (!el || !(window as any).lucide) return;
  el.innerHTML = `<i data-lucide="${iconName}" style="width:${size}px;height:${size}px"></i>`;
  (window as any).lucide.createIcons({ nodes: [el] });
}

export class ImmersiveSkin {
  private _views: IView[] = [];

  constructor(private _sessionName: string) {}

  mount(root: HTMLElement): void {
    root.innerHTML = templateHtml;

    this._views = [
      new TopicCardView(),
      new VibePillView(),
      new SpecialTrackView(),
      new YoutubePanelView(),
    ];

    for (const view of this._views) {
      view.mount(root);
    }

    // Settings modal
    const gearBtn = root.querySelector<HTMLElement>('#immGearBtn');
    const modal = root.querySelector<HTMLElement>('#immSettingsModal');
    const backdrop = root.querySelector<HTMLElement>('#immModalBackdrop');
    const closeBtn = root.querySelector<HTMLElement>('#immModalClose');
    const toggleModal = () => {
      if (modal) modal.style.display = modal.style.display === 'none' ? 'block' : 'none';
    };
    gearBtn?.addEventListener('click', toggleModal);
    backdrop?.addEventListener('click', toggleModal);
    closeBtn?.addEventListener('click', toggleModal);

    // KnowledgeView modal — injected once at mount, opened on demand
    // by the "Mehr erfahren" button that appears in the audio panel's
    // actions when topic.knowledge_id is present. One reusable host
    // div keeps the GLB-cleanup story sane (single dispose, single
    // teardown). Modal uses the same backdrop pattern as Settings.
    const knowledgeModal = document.createElement('div');
    knowledgeModal.id = 'immKnowledgeModal';
    knowledgeModal.className = 'imm-modal kv-modal';
    knowledgeModal.style.display = 'none';
    knowledgeModal.innerHTML = `
      <div class="imm-modal-backdrop" data-close-knowledge="1"></div>
      <div class="imm-modal-panel kv-modal-panel">
        <button class="imm-modal-close" data-close-knowledge="1" aria-label="Schliessen">✕</button>
        <div id="immKnowledgeHost"></div>
      </div>
    `;
    root.appendChild(knowledgeModal);

    let knowledgeView: KnowledgeView | null = null;
    const closeKnowledge = () => {
      knowledgeModal.style.display = 'none'; // legacy chrome, stays hidden
      document.body.classList.remove('kv-open');
      (this as any)._kvOpenKid = null; // welches Item zeigt die offene View
      if (knowledgeView) {
        knowledgeView.destroy();
        knowledgeView = null;
      }
      requestAnimationFrame(() => (this as any)._adjustControls?.());
    };
    (this as any)._closeKnowledge = closeKnowledge; // für den topic:changed-Scope
    knowledgeModal.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target?.dataset?.closeKnowledge === '1') closeKnowledge();
    });

    const openKnowledge = async (key: { binom?: string; knowledge_id?: number }) => {
      const data = await fetchKnowledge(key);
      if (!data) return;
      if (knowledgeView) knowledgeView.destroy();
      knowledgeView = new KnowledgeView(data, {
        // Same cloud-api the warmup TTS uses (main.ts:1009) — gives
        // annotations WITHOUT pre-rendered audio an on-demand voice.
        ttsApiBase: (import.meta.env.VITE_CLOUD_API_BOOTSTRAP as string)
          || 'https://cloud-api.arkserver.arkturian.com',
        // Immersive layout owns its own back button — route it to the
        // modal teardown so destroy() + host bookkeeping stay in one
        // place.
        onClose: closeKnowledge,
      });
      // Direkt auf document.body — NICHT in den Modal-Host: dessen
      // Panel hat transform:translate(-50%,-50%), und ein transform
      // macht den Ahnen zum Containing-Block für position:fixed. Die
      // Fullscreen-View war dadurch in der Modal-Box gefangen und
      // unsichtbar (Auto-Open-Debug 2026-06-12: open=true, nichts zu
      // sehen). Das Modal-Chrome wird für Knowledge nicht mehr genutzt.
      knowledgeView.mount(document.body);
      (this as any)._kvOpenKid = typeof key.knowledge_id === 'number' ? key.knowledge_id : null;
      // Stop/Record/Foto-Leiste über der Wissens-Vollbildansicht halten —
      // muss auch in der 3D-Ansicht erreichbar bleiben (CSS body.kv-open) —
      // und an die Panel-Oberkante positionieren.
      document.body.classList.add('kv-open');
      requestAnimationFrame(() => (this as any)._adjustControls?.());
    };

    // Expose the opener so the topic:changed handler below can call it.
    (this as any)._openKnowledge = openKnowledge;
    (this as any)._isKnowledgeOpen = (): boolean => knowledgeView !== null;

    // Initialize Lucide icons
    if ((window as any).lucide) {
      (window as any).lucide.createIcons();
    }

    // Waveform bars
    const waveformContainer = root.querySelector<HTMLElement>('#waveformBars');
    if (waveformContainer) {
      for (let i = 0; i < 30; i++) {
        const bar = document.createElement('div');
        bar.className = 'wave-bar';
        bar.style.height = `${3 + Math.random() * 12}px`;
        waveformContainer.appendChild(bar);
      }
    }
  }

  bindEvents(bus: EventManager): void {
    // Knowledge auto-open state: last GPS fix + once-per-session set.
    let lastFix: { lat: number; lon: number } | null = null;
    const autoOpenedKids = new Set<number>();
    const haversineM = (la1: number, lo1: number, la2: number, lo2: number): number => {
      const R = 6371000;
      const dLa = ((la2 - la1) * Math.PI) / 180;
      const dLo = ((lo2 - lo1) * Math.PI) / 180;
      const a = Math.sin(dLa / 2) ** 2
        + Math.cos((la1 * Math.PI) / 180) * Math.cos((la2 * Math.PI) / 180) * Math.sin(dLo / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(a));
    };
    const remoteLogSafe = (msg: string): void => {
      try {
        void fetch('https://log-api.arkturian.com/api/v1/sessions/audio-guide/logs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ entries: [{ type: 'Log', message: msg, source: 'knowledge' }] }),
        });
      } catch { /* fire-and-forget */ }
    };

    for (const view of this._views) {
      view.bindEvents(bus);
    }

    const centerArea = document.getElementById('centerArea');
    const welcomeContent = document.getElementById('welcomeContent');
    const statusContent = document.getElementById('statusContent');
    const statusText = document.getElementById('statusText');
    const statusDetail = document.getElementById('statusDetail');
    const statusSession = document.getElementById('statusSession');
    const startBtn = document.getElementById('startBtn');
    const audioPlayer = document.getElementById('audioPlayerCard');
    const playerTitle = document.getElementById('playerTitle');
    const playerSubtitle = document.getElementById('playerSubtitle');
    const playerCategory = document.getElementById('playerCategory');
    const playerLocation = document.getElementById('playerLocation');
    const panelActions = document.getElementById('panelActions');
    const waveformBars = document.querySelectorAll<HTMLElement>('.wave-bar');

    // START → show intent input instead of starting directly
    const intentArea = document.getElementById('intentArea');
    const intentInput = document.getElementById('intentInput') as HTMLInputElement | null;
    const intentGoBtn = document.getElementById('intentGoBtn');
    const intentSkipBtn = document.getElementById('intentSkipBtn');

    const startGuide = (intent?: string) => {
      if (intentArea) intentArea.style.display = 'none';
      if (intent) {
        // Store intent — will be sent with first GPS message
        (window as any).__guideIntent = intent;
      }
      bus.emit('user:start');
    };

    startBtn?.addEventListener('click', () => {
      if (startBtn) startBtn.classList.add('hidden');
      if (intentArea) intentArea.style.display = 'flex';
      intentInput?.focus();
    });

    intentGoBtn?.addEventListener('click', () => {
      startGuide(intentInput?.value?.trim() || undefined);
    });

    intentSkipBtn?.addEventListener('click', () => {
      startGuide();
    });

    intentInput?.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') startGuide(intentInput.value?.trim() || undefined);
    });
    document.getElementById('stopBtn')?.addEventListener('click', () => bus.emit('user:stop'));
    document.getElementById('rebootBtn')?.addEventListener('click', () => bus.emit('user:reboot-guide'));
    document.getElementById('skinSwitchBtn')?.addEventListener('click', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('skin', 'admin');
      window.location.href = url.toString();
    });

    // Interval slider
    const intervalSlider = document.getElementById('intervalSlider') as HTMLInputElement | null;
    const intervalLabel = document.getElementById('intervalLabel');
    intervalSlider?.addEventListener('input', () => {
      const val = parseInt(intervalSlider.value, 10);
      if (intervalLabel) intervalLabel.textContent = `${val}s`;
      bus.emit('user:interval-change', { seconds: val });
    });

    // OSM toggle
    let osmEnabled = false;
    const osmToggle = document.getElementById('osmToggle');
    const osmRadiusSlider = document.getElementById('osmRadiusSlider') as HTMLInputElement | null;
    const osmRadiusLabel = document.getElementById('osmRadiusLabel');
    osmToggle?.addEventListener('click', () => {
      osmEnabled = !osmEnabled;
      osmToggle.textContent = osmEnabled ? '📍 Places: ON' : '📍 Places: OFF';
      if (osmRadiusSlider) { osmRadiusSlider.style.display = osmEnabled ? 'block' : 'none'; osmRadiusSlider.disabled = !osmEnabled; }
      if (osmRadiusLabel) osmRadiusLabel.style.display = osmEnabled ? 'block' : 'none';
      bus.emit('user:osm-toggle', { enabled: osmEnabled });
    });
    osmRadiusSlider?.addEventListener('input', () => {
      const r = parseInt(osmRadiusSlider.value, 10);
      if (osmRadiusLabel) osmRadiusLabel.textContent = r >= 1000 ? `${(r/1000).toFixed(1)}km` : `${r}m`;
      bus.emit('user:osm-radius', { radius: r });
    });

    // Transport + mode buttons
    // Transport + mode buttons are wired in main.ts bootstrap
    // (before guide exists) so they work from the welcome screen.

    // ── Layout: push tour controls above the audio panel footer ──
    const actionsArea = document.getElementById('actionsArea');
    const adjustControlsPosition = () => {
      if (!audioPlayer) return;
      // Wissens-Vollbildansicht offen: Controls an die OBERKANTE des
      // Wissens-Panels setzen (ragen in die Bildkante als Indikator). Das
      // Panel ist bei Flora/Fauna höher → dynamisch aus seiner Höhe messen.
      if (document.body.classList.contains('kv-open')) {
        const kvPanel = document.querySelector<HTMLElement>('.kv-exp-panel');
        if (kvPanel && kvPanel.offsetHeight > 0 && actionsArea) {
          actionsArea.style.bottom = `${kvPanel.offsetHeight - 18}px`;
        }
        return;
      }
      if (audioPlayer.style.display !== 'none' && audioPlayer.offsetHeight > 0) {
        const h = audioPlayer.offsetHeight;
        if (actionsArea) actionsArea.style.bottom = `${h + 8}px`;
        // Push topic image above the footer so it's fully visible
        document.documentElement.style.setProperty('--footer-h', `${h}px`);
      } else {
        if (actionsArea) actionsArea.style.bottom = '';
        document.documentElement.style.setProperty('--footer-h', '0px');
      }
    };
    (this as any)._adjustControls = adjustControlsPosition; // aus open/closeKnowledge aufrufbar

    // ── State transitions (same screen, content morphs) ──

    const tourControls = document.getElementById('tourControls');

    // On start: welcome → status info, show tour controls
    let hasStartedOnce = false;

    bus.on('tracking:changed', (snap: { isRunning: boolean }) => {
      if (snap.isRunning) {
        if (welcomeContent) welcomeContent.style.display = 'none';
        if (intentArea) intentArea.style.display = 'none';
        if (startBtn) startBtn.classList.add('hidden');
        if (tourControls) tourControls.style.display = 'flex';

        if (hasStartedOnce) {
          if (statusContent) statusContent.style.display = 'none';
          if (centerArea) centerArea.classList.add('faded');
        } else {
          hasStartedOnce = true;
          if (statusContent) statusContent.style.display = 'block';
          if (statusSession) statusSession.textContent = t('status.guideStartingDetail', { session: this._sessionName });
          if (statusText) statusText.textContent = t('status.guideWaking');
          if (statusDetail) statusDetail.textContent = t('status.guideWakingDetail');
        }
      } else {
        // STOPPED — back to welcome
        if (welcomeContent) welcomeContent.style.display = 'block';
        if (statusContent) statusContent.style.display = 'none';
        if (startBtn) startBtn.classList.remove('hidden');
        if (tourControls) tourControls.style.display = 'none';
        if (centerArea) centerArea.classList.remove('faded');
        if (audioPlayer) audioPlayer.style.display = 'none';
        if (actionsArea) actionsArea.style.bottom = '';
        document.documentElement.style.setProperty('--footer-h', '0px');
      }
    });

    // First GPS sent → update status
    let sentCount = 0;
    bus.on('log:message', (entry: { text: string }) => {
      if (entry.text?.includes('→ Sent')) {
        sentCount++;
        if (sentCount === 1 && statusText && statusDetail) {
          statusText.textContent = t('status.firstSent');
          statusDetail.textContent = t('status.firstSentDetail');
        }
      }
    });

    // Fade out the welcome/status area as soon as the bot answers
    bus.on('iacp:response', () => {
      if (centerArea) centerArea.classList.add('faded');
    });

    // Bottom panel text must stay in sync with whatever TTS is currently
    // playing — that's the `package:start` event (fires when a package
    // becomes active, just before TTS speaks its content). Listening to
    // iacp:response here was wrong for multi-package responses: it set
    // responseText to Topic #1's text and never updated when the queue
    // advanced to Topic #2/#3.
    bus.on('package:start', (payload: { content: string }) => {
      const text = document.getElementById('responseText');
      if (text && payload?.content) text.textContent = payload.content;
    });

    // Gallery-Slideshow-Timer (Content #1173): bei images[] mit 2–3 Bildern
    // auto-advance; bei jedem topic:changed gestoppt. 1 Bild = statisch.
    let slideshowTimer: ReturnType<typeof setInterval> | null = null;
    // Topic → update audio panel + background image
    bus.on('topic:changed', (payload: { topic: any }) => {
      if (!payload.topic || !audioPlayer) return;

      // Neuer Beitrag spielt → eine offene (3D-)Wissensansicht eines ANDEREN
      // Items schließen, sonst bleibt sie über der nächsten Narration stehen.
      // Dasselbe Item (gleiche kid) lässt die View offen.
      const incomingKid = typeof payload.topic.knowledge_id === 'number'
        ? payload.topic.knowledge_id : null;
      if ((this as any)._isKnowledgeOpen?.() === true && incomingKid !== (this as any)._kvOpenKid) {
        (this as any)._closeKnowledge?.();
      }

      // Update the hero background image (#topicImg). Previously this was
      // only set in main.ts during session-restore, which left the live
      // tour flow with a stale or empty <img> — when data-view flipped to
      // 'topic' the user saw the body's dark background and nothing else.
      // Now every topic:changed either swaps in a fresh image or hides the
      // wrapper so the map shows through.
      const topicImg = document.getElementById('topicImg') as HTMLImageElement | null;
      const topicImgWrap = document.getElementById('topicImgWrap');
      if (topicImg && topicImgWrap) {
        // Bilder-Liste: Gallery (topic.images, Content #1173) wenn vorhanden,
        // sonst Einzelbild (topic.image). Graceful 1..3.
        const imgs: string[] = Array.isArray(payload.topic.images) && payload.topic.images.length
          ? (payload.topic.images as unknown[]).filter((u): u is string => typeof u === 'string' && u.length > 0)
          : (payload.topic.image ? [payload.topic.image as string] : []);
        // Laufende Slideshow immer stoppen (neuer Beitrag / Einzelbild).
        if (slideshowTimer !== null) { clearInterval(slideshowTimer); slideshowTimer = null; }

        // Tote URLs (404/CORS) dürfen nicht "kein Bild" bedeuten, solange die
        // Gallery noch Kandidaten hat: onerror rückt zum nächsten Bild nach;
        // erst wenn ALLE Kandidaten tot sind, blendet der Wrapper aus (Karte
        // scheint durch = deterministisches letztes Glied der Kette).
        const failed = new Set<string>();
        const showImage = (src: string): void => {
          if (topicImg.getAttribute('src') === src) return;
          topicImgWrap.classList.add('topic-image-loading');
          topicImg.onload = () => topicImgWrap.classList.remove('topic-image-loading');
          topicImg.onerror = () => {
            topicImgWrap.classList.remove('topic-image-loading');
            failed.add(src);
            const next = imgs.find((u) => !failed.has(u));
            if (next) {
              showImage(next);
            } else {
              topicImgWrap.style.display = 'none';
            }
          };
          topicImg.src = src;
        };

        if (imgs.length === 0) {
          topicImg.removeAttribute('src');
          topicImgWrap.style.display = 'none';
        } else {
          showImage(imgs[0]);
          topicImgWrap.style.display = 'flex';
          // Gallery (2–3 Bilder) → auto-advance alle 4.5s. 1 Bild = statisch (kein Chrome).
          if (imgs.length > 1) {
            let idx = 0;
            slideshowTimer = setInterval(() => {
              idx = (idx + 1) % imgs.length;
              showImage(imgs[idx]);
            }, 4500);
          }
        }
      }

      if (playerTitle) playerTitle.textContent = payload.topic.title || '';
      if (playerSubtitle) playerSubtitle.textContent = payload.topic.subtitle || '';
      if (playerCategory) {
        const tags = payload.topic.tags;
        playerCategory.textContent = Array.isArray(tags) && tags.length > 0
          ? tags[0].replace(/^[^\w]*/, '') : '';
      }
      if (panelActions) {
        panelActions.innerHTML = '';
        if (payload.topic.wikipedia) {
          const a = document.createElement('a');
          a.href = payload.topic.wikipedia; a.target = '_blank'; a.rel = 'noopener';
          a.textContent = t('topic.wikipedia');
          panelActions.appendChild(a);
        }
        // Image attribution — Wikimedia/Commons is rights-clean with link,
        // other sources need their original host as attribution.
        const imgUrl: string = payload.topic.image || '';
        if (imgUrl && /^https?:/.test(imgUrl)) {
          try {
            const host = new URL(imgUrl).hostname.replace(/^www\./, '');
            // Skip if it's the same Wikipedia host already shown
            const wikiHost = payload.topic.wikipedia ? new URL(payload.topic.wikipedia).hostname.replace(/^www\./, '') : '';
            const isWikimedia = /wikipedia\.org|wikimedia\.org/.test(host);
            if (!(isWikimedia && wikiHost && /wikipedia\.org|wikimedia\.org/.test(wikiHost))) {
              const friendly: Record<string, string> = {
                'upload.wikimedia.org': 'Wikimedia Commons',
                'commons.wikimedia.org': 'Wikimedia Commons',
                'lh3.googleusercontent.com': 'Google Places',
                'maps.googleapis.com': 'Google Places',
              };
              const label = friendly[host] || host;
              const b = document.createElement('a');
              b.href = imgUrl; b.target = '_blank'; b.rel = 'noopener';
              b.textContent = label;  // Just the source name, no icon
              b.className = 'imm-image-attr';
              b.title = t('topic.imageSource', { label });
              panelActions.appendChild(b);
            }
          } catch { /* malformed URL → skip attribution */ }
        }

        // Knowledge drill-down link — appears whenever the bot tagged
        // a topic with a knowledge_id (from knowledge_near results) or
        // its subtitle is a binomial-shaped string ("Genus species"
        // pattern). The button fetches the full species record from
        // knowledge-api on click and opens the KnowledgeView modal
        // with 2D illustration + annotation pins (and optionally a
        // lazy-loaded 3D GLB renderer when a 3D-model media item is
        // attached).
        const subtitleStr: string = String(payload.topic.subtitle || '');
        const looksLikeBinom = /^[A-Z][a-z]+ [a-z]+(?:\s+(?:subsp|var|f|ssp)\.?)?/.test(subtitleStr);
        const kid: number | undefined = typeof payload.topic.knowledge_id === 'number'
          ? payload.topic.knowledge_id
          : undefined;
        const binom: string | undefined = looksLikeBinom ? subtitleStr.split(/\s+/).slice(0, 2).join(' ') : undefined;
        const wid: number | undefined = typeof payload.topic.waypoint_id === 'number'
          ? payload.topic.waypoint_id
          : undefined;

        const addKnowledgeButton = (key: { binom?: string; knowledge_id?: number }) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'kv-open-btn';
          btn.textContent = t('topic.knowledge') || 'Mehr erfahren';
          btn.addEventListener('click', () => {
            const opener = (this as any)._openKnowledge as (k: { binom?: string; knowledge_id?: number }) => void;
            opener?.(key);
          });
          panelActions.appendChild(btn);
        };

        if (kid || binom) {
          // Direct path: bot already gave us the knowledge anchor.
          addKnowledgeButton({ binom, knowledge_id: kid });
        } else if (wid) {
          // Indirect path: bot only gave waypoint_id (e.g. screen_point
          // with linked Knowledge that the bot didn't drill into).
          // Async lookup artrack → if the waypoint carries
          // metadata_json.knowledge_id, light up the button with the
          // discovered kid. Button appears after the lookup completes
          // (typically <200ms on cached entries) so the topic-card may
          // briefly render without it on the first paint — acceptable
          // since the user reads/hears the content first anyway.
          void fetchArtrackWaypoint(wid).then((wp) => {
            const discoveredKid = wp?.metadata_json?.knowledge_id;
            if (typeof discoveredKid === 'number' && discoveredKid > 0) {
              addKnowledgeButton({ knowledge_id: discoveredKid });
            }
          });
        }

        // AUTO-OPEN am Knowledge-Point (Alex 2026-06-12): Liefert der
        // Bot ein Topic mit knowledge_id UND steht der User physisch
        // am Punkt (≤30m — die Stufe-3-Schwelle aus guide.md), öffnet
        // die Experience direkt — kein Klick nötig. Einmal pro Item
        // und Session: Zumachen wird respektiert, der nächste Tick
        // drängt dasselbe Item nicht wieder auf. Der Button oben
        // bleibt als manueller (Wieder-)Einstieg, auch für ferne
        // Knowledge-Topics. topic.lat/lon ist die POI-Position
        // (guide.md KRITISCH #3), nie die User-Position — die Distanz
        // ist also echt.
        const tLat = payload.topic.lat;
        const tLon = payload.topic.lon;
        if (
          kid &&
          !autoOpenedKids.has(kid) &&
          lastFix &&
          typeof tLat === 'number' &&
          typeof tLon === 'number' &&
          !((this as any)._isKnowledgeOpen?.() === true)
        ) {
          const distM = haversineM(lastFix.lat, lastFix.lon, tLat, tLon);
          if (distM <= 30) {
            autoOpenedKids.add(kid);
            remoteLogSafe(`Knowledge auto-open: kid=${kid} dist=${Math.round(distM)}m`);
            const opener = (this as any)._openKnowledge as (k: { knowledge_id?: number }) => void;
            opener?.({ knowledge_id: kid });
          }
        }
      }
      audioPlayer.style.display = 'block';
      requestAnimationFrame(adjustControlsPosition);
    });

    // Location → update panel location + remember the fix for the
    // knowledge auto-open proximity check.
    bus.on('location:changed', (loc: any) => {
      if (playerLocation && loc?.city) playerLocation.textContent = loc.city;
      if (typeof loc?.lat === 'number' && typeof loc?.lon === 'number') {
        lastFix = { lat: loc.lat, lon: loc.lon };
      }
    });

    // TTS waveform animation — driven by the REAL audio signal via an
    // AnalyserNode tap on the TTS pipeline. If you see flat bars while
    // TTS is supposedly playing, it means no samples are reaching the
    // speakers (suspended AudioContext, Silent-Switch, volume zero,
    // OS-level ducking by another app). This honesty is the whole point —
    // the previous Math.random() animation was lying to the user.
    let waveformFrame: number | null = null;
    let staleCounter = 0;
    bus.on('tts:start', () => {
      if (audioPlayer) audioPlayer.style.display = 'block';
      requestAnimationFrame(adjustControlsPosition);

      const analyser = (window as any).__audioGuide?.ttsAnalyser as AnalyserNode | null;
      if (!analyser) {
        // Lib not wired yet — fall back to flat bars so we at least don't lie.
        waveformBars.forEach(b => { b.style.height = '4px'; b.classList.remove('active'); });
        return;
      }
      const bins = new Uint8Array(analyser.frequencyBinCount);
      const N = waveformBars.length;
      staleCounter = 0;

      const tick = () => {
        analyser.getByteFrequencyData(bins);
        // Aggregate frequency bins into N display bars
        const slice = Math.max(1, Math.floor(bins.length / N));
        let totalEnergy = 0;
        waveformBars.forEach((bar, i) => {
          let sum = 0;
          for (let j = 0; j < slice; j++) sum += bins[i * slice + j] ?? 0;
          const avg = sum / slice;        // 0..255
          totalEnergy += avg;
          const h = 3 + (avg / 255) * 28; // 3..31 px
          bar.style.height = `${h.toFixed(0)}px`;
          bar.classList.toggle('active', avg > 36);
        });
        // Detect "fake play" — TTS state says playing but the signal is
        // dead for too many consecutive frames. Show a one-time hint.
        if (totalEnergy < 1) {
          staleCounter++;
          if (staleCounter === 90) { // ~1.5s of pure silence
            const ctx = (window as any).__audioGuide?.audioContext as AudioContext | null;
            const hint = ctx?.state === 'suspended'
              ? t('toast.audioPaused')
              : t('toast.audioSilent');
            (window as any).__guideShowToast?.(hint);
          }
        } else {
          staleCounter = 0;
        }
        waveformFrame = requestAnimationFrame(tick);
      };
      waveformFrame = requestAnimationFrame(tick);
    });

    bus.on('tts:end', () => {
      if (waveformFrame !== null) { cancelAnimationFrame(waveformFrame); waveformFrame = null; }
      waveformBars.forEach(bar => {
        bar.style.height = '4px';
        bar.classList.remove('active');
      });
    });

    // View toggle — always visible during tour, switches map ↔ topic
    //
    // Single source of truth: every state change (button click, auto-switch
    // in main.ts on topic:changed, auto-switch back on tts:end) flows
    // through body[data-view]. A MutationObserver below mirrors that
    // attribute into the local isImageView flag AND syncs the button icon
    // — so external setters (main.ts) and this skin never desync.
    //
    // Icon convention: shows the icon of the TARGET view ("tap to go to
    // map" = map icon when in topic-view, image icon when in map-view).
    const viewToggleBtn = document.getElementById('viewToggleBtn');
    let isImageView = false;
    let hasCurrentImage = false;

    const syncIconToCurrentView = (): void => {
      if (!viewToggleBtn) return;
      const view = document.body.getAttribute('data-view') || 'map';
      setLucideIcon(viewToggleBtn, view === 'topic' ? 'map' : 'image', 18);
      viewToggleBtn.style.opacity = hasCurrentImage || view === 'topic' ? '1' : '0.3';
    };

    if (viewToggleBtn) {
      viewToggleBtn.addEventListener('click', () => {
        if (isImageView) {
          // Image → Map: always works
          document.body.setAttribute('data-view', 'map');
        } else if (hasCurrentImage) {
          // Map → Image: only if there's an image
          document.body.setAttribute('data-view', 'topic');
        }
        // Icon + isImageView are reconciled by the MutationObserver below.
      });
    }

    // Mirror body[data-view] → local state + button icon. Fires for both
    // own clicks above and external setters (main.ts auto-switch on
    // topic:changed + tts:end). Without this observer the icon would
    // freeze on whatever the last click rendered and stop matching the
    // actual view.
    const viewObserver = new MutationObserver(() => {
      const view = document.body.getAttribute('data-view') || 'map';
      isImageView = (view === 'topic');
      syncIconToCurrentView();
    });
    viewObserver.observe(document.body, { attributes: true, attributeFilter: ['data-view'] });

    // Track whether current topic has an image so the button can dim when
    // map→image would be a no-op. Auto-switch to image view for hero
    // photos is now owned exclusively by main.ts (see
    // bus.on('topic:changed') around line ~2229) — we removed the
    // duplicate auto-switch here that used to fight with it.
    bus.on('topic:changed', (payload: { topic: any }) => {
      hasCurrentImage = !!(payload.topic?.image);
      syncIconToCurrentView();
    });

    // Mic button — tap to record, tap again to send
    const micBtn = document.getElementById('micBtn');
    const photoBtn = document.getElementById('photoBtn');
    const photoInput = document.getElementById('photoInput') as HTMLInputElement | null;

    if (micBtn) {
      // Press-hold gesture: a quick TAP toggles voice recording (the
      // existing audio path); HOLDING ≥ 500ms enters video recording
      // and records while held — release stops + uploads. The two
      // gestures share one button so the bottom bar stays uncluttered.
      // All state lives in main.ts (voice/video recording flags); we
      // only emit the raw gestures here.
      const HOLD_MS = 500;
      let holdTimer: ReturnType<typeof setTimeout> | null = null;
      let videoGesture = false;
      micBtn.addEventListener('pointerdown', (ev) => {
        ev.preventDefault();
        videoGesture = false;
        holdTimer = setTimeout(() => {
          videoGesture = true;
          micBtn.classList.add('recording', 'recording-video');
          bus.emit('user:video-start');
        }, HOLD_MS);
      });
      const release = (): void => {
        if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
        if (videoGesture) {
          videoGesture = false;
          micBtn.classList.remove('recording-video', 'recording');
          bus.emit('user:video-stop');
        } else {
          // Quick tap → audio toggle (start, or stop+send if already on).
          bus.emit('user:mic-toggle');
          micBtn.classList.toggle('recording');
        }
      };
      micBtn.addEventListener('pointerup', release);
      micBtn.addEventListener('pointercancel', () => {
        if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
        if (videoGesture) {
          videoGesture = false;
          micBtn.classList.remove('recording-video', 'recording');
          bus.emit('user:video-stop');
        }
      });
    }

    // Photo button — click handler is bound ONCE in main.ts (which also
    // owns the file-input change listener). A duplicate binding here was
    // calling photoInput.click() twice per tap; on iOS Safari the second
    // .click() can interrupt the first camera dialog and the change event
    // never fires for the picked photo, so the upload silently dies.
    void photoBtn; void photoInput;

    // Music control — circle morphs into a pill (same border, same bg,
    // just wider) with two inner buttons: Pause/Play (the original music
    // button itself toggles) and Next.
    const musicToggleBtn = document.getElementById('musicToggleBtn');
    const topRightControls = document.getElementById('topRightControls');
    const musicControl = document.getElementById('musicControl');
    const musicNextBtn = document.getElementById('musicPillNextBtn');
    let musicPaused = false;
    let musicExpanded = false;
    let collapseTimer: number | null = null;

    const collapseMusic = () => {
      musicExpanded = false;
      musicControl?.classList.remove('expanded');
    };
    const expandMusic = () => {
      if (musicExpanded) return;
      musicExpanded = true;
      musicControl?.classList.add('expanded');
      // Auto-collapse after 4s of no interaction
      if (collapseTimer !== null) window.clearTimeout(collapseTimer);
      collapseTimer = window.setTimeout(collapseMusic, 4000);
      // Tap-outside-to-collapse
      const onOutsideTap = (e: Event) => {
        if (!musicControl?.contains(e.target as Node)) {
          collapseMusic();
          document.removeEventListener('click', onOutsideTap, true);
          document.removeEventListener('touchstart', onOutsideTap, true);
        }
      };
      window.setTimeout(() => {
        document.addEventListener('click', onOutsideTap, true);
        document.addEventListener('touchstart', onOutsideTap, true);
      }, 50);
    };
    const resetCollapseTimer = () => {
      if (collapseTimer !== null) window.clearTimeout(collapseTimer);
      collapseTimer = window.setTimeout(collapseMusic, 4000);
    };

    // Tap on the music button:
    //   first tap (collapsed) → expand to pill
    //   subsequent tap (expanded) → toggle Pause/Play
    if (musicToggleBtn) {
      musicToggleBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!musicExpanded) {
          expandMusic();
          return;
        }
        musicPaused = !musicPaused;
        setLucideIcon(musicToggleBtn, musicPaused ? 'play' : 'pause', 18);
        bus.emit(musicPaused ? 'user:music-off' : 'user:music-on');
        resetCollapseTimer();
      });
    }

    musicNextBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      bus.emit('user:music-skip');
      musicNextBtn.style.transform = 'scale(0.85)';
      setTimeout(() => { musicNextBtn.style.transform = ''; }, 120);
      resetCollapseTimer();
    });

    // Show top-right controls when tour starts — keep visible during pause
    let tourStarted = false;
    bus.on('tracking:changed', (snap: { isRunning: boolean }) => {
      if (snap.isRunning) tourStarted = true;
      if (topRightControls && tourStarted) {
        topRightControls.style.display = 'flex';
      }
    });
    // Hide on stop
    bus.on('user:stop', () => {
      tourStarted = false;
      if (topRightControls) topRightControls.style.display = 'none';
    });
  }

  dispose(): void {
    for (const view of this._views) { view.dispose(); }
    this._views = [];
  }
}
